import React, { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { ScreenState } from './ScreenState';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { errorFallbackProps, goHomeFromErrorScreen } from '../../utils/errorScreenHome';

type ErrorFallbackProps = {
    error: Error;
    /** Clears the boundary's error so the children render again. */
    onRetry: () => void;
};

/**
 * CORE-1517 — what the root ErrorBoundary shows.
 *
 * Online: «Попробовать снова» re-renders the tree (a failed screen chunk is
 * imported afresh — see lazyScreen), and on web «На главную» is a full
 * document navigation to «/» that depends neither on the crashed tree nor on
 * the broken chunk. The raw message is for dev builds only; Sentry has it.
 *
 * Offline: the screen says so instead of «что-то пошло не так», offers no
 * buttons (both would only fail again, «/» on the browser's own offline page),
 * and retries by itself the moment the network is back — the person does
 * nothing. Nothing here reloads the page on its own (CORE-714 holdback).
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onRetry }) => {
    const online = useOnlineStatus();
    const wasOffline = useRef(!online);

    useEffect(() => {
        if (!online) {
            wasOffline.current = true;
            return;
        }
        if (wasOffline.current) {
            wasOffline.current = false;
            onRetry();
        }
    }, [online, onRetry]);

    const copy = errorFallbackProps({ error, online, isWeb: Platform.OS === 'web', isDev: __DEV__ });

    const goHome = () => {
        if (Platform.OS !== 'web') return;
        goHomeFromErrorScreen(typeof window !== 'undefined' ? window.location : undefined);
    };

    return (
        <ScreenState
            status="error"
            message={copy.message}
            description={copy.description}
            actionLabel={copy.actionLabel}
            onAction={copy.actionLabel ? onRetry : undefined}
            secondaryActionLabel={copy.secondaryActionLabel}
            onSecondaryAction={copy.secondaryActionLabel ? goHome : undefined}
        />
    );
};
